import type { LedgerEntry } from '@frontend/api-types';
import { dateFormatters, formatNumber } from '@frontend/duck-tape';
import { transformCreditDescription, type SubscriptionTerminologyConfig } from '@frontend/react-domain';
import { Divider, EmptyList, FlatList, Icon, Modal, SkeletonList, Text, XStack, YStack } from '@frontend/web-react';

export type CreditHistoryModalProps = {
  closeModal: () => void;
  isError: boolean;
  isLoading: boolean;
  isOpen: boolean;
  ledgerEntries: LedgerEntry[];
  pricingTerminology: SubscriptionTerminologyConfig;
  showBackButton?: boolean;
  subscriptionRenewalCredits?: number;
};

export const CreditHistoryModal = ({
  closeModal,
  isError,
  isLoading,
  isOpen,
  ledgerEntries,
  pricingTerminology,
  showBackButton = false,
  subscriptionRenewalCredits = 0,
}: CreditHistoryModalProps) => {
  const showCredits = !pricingTerminology.hide_credits;
  const title = pricingTerminology.history_title;
  return (
    <Modal
      Header={
        <YStack>
          <XStack className="p-md items-center justify-center relative">
            <Text color="secondary" type="h5">
              {title}
            </Text>
            {showBackButton ? (
              <Icon
                accessibilityLabel="Go back to account details"
                canFocus
                className="absolute left-md m-auto"
                enableBackgroundColor={false}
                name="IconChevronLeft"
                onClick={closeModal}
                size="lg"
              />
            ) : null}
            <Icon
              accessibilityLabel="Close modal"
              canFocus
              className="absolute right-md m-auto"
              enableBackgroundColor={false}
              name="IconX"
              onClick={closeModal}
            />
          </XStack>
          <Divider />
        </YStack>
      }
      headerGap="none"
      isOpen={isOpen}
      onClose={closeModal}
    >
      {isError || (ledgerEntries && !ledgerEntries?.length) ? (
        <YStack className="w-full items-center" gap={'sm'}>
          <Icon color={'neutral'} name="IconMug" />
          <Text color="tertiary" type="p-sm">
            {isError || !ledgerEntries
              ? `Looks like your ${pricingTerminology.history_title.toLowerCase()} didn't load—try again?`
              : `No ${pricingTerminology.history_title.toLowerCase()} found.`}
          </Text>
        </YStack>
      ) : (
        <FlatList
          ListEmptyComponent={
            <EmptyList
              iconName="IconAlertCircle"
              label={`Unable to load ${pricingTerminology.history_title.toLowerCase()}.`}
              subLabel="Check back later"
            />
          }
          ListLoadingComponent={<SkeletonList countSkeletons={3} />}
          accessibilityLabel="History list"
          contentContainerClassName={'gap-none'}
          data={ledgerEntries}
          isLoading={isLoading}
          keyExtractor={(item) => `${item.eventTs}-${item.entryType}`}
          renderItem={({ array, index, item }) => {
            const percentageDisplay = subscriptionRenewalCredits
              ? `${item.amount > 0 ? '+' : ''}${Math.round((Math.abs(item.amount) / subscriptionRenewalCredits) * 100)}%`
              : '0%';

            return (
              <YStack>
                <XStack className={'items-center justify-between py-md'}>
                  <YStack>
                    <Text color="primary" type="p-sm">
                      {transformCreditDescription(item.description, pricingTerminology)}
                    </Text>
                    <Text color="tertiary" type="caption">
                      {dateFormatters['M/D'](item.eventTs)}
                    </Text>
                  </YStack>
                  {showCredits ? (
                    <Text color={item.amount > 0 ? 'success' : 'primary'} type="h6">
                      {formatNumber(item.amount, { signDisplay: 'always' })}
                    </Text>
                  ) : (
                    <Text
                      className={item.amount > 0 ? 'text-green-700' : undefined}
                      color={item.amount > 0 ? undefined : 'primary'}
                      type="h6"
                    >
                      {percentageDisplay}
                    </Text>
                  )}
                </XStack>
                <Divider forList index={index} list={array} />
              </YStack>
            );
          }}
        />
      )}
    </Modal>
  );
};
